/**
 * Designer theme — aligned with the unified appColors palette.
 * Source of truth: src/themes/colors.ts
 *
 * The designer always uses dark mode. Colors are derived from appColors
 * so that switching between Designer ↔ App mode feels seamless.
 */

import { createTheme, alpha } from '@mui/material/styles'
import { appColors } from '../../themes/colors'

// Short aliases
const P = appColors.primary
const S = appColors.secondary
const TX = appColors.text.dark

declare module '@mui/material/styles' {
  interface Palette {
    sqlZone: { background: string; border: string }
    jsZone: { background: string; border: string }
    sysProc: { readonlyBg: string; overriddenBg: string }
    treeNode: { dirtyDot: string }
    cellType: {
      text: string
      parameter: string
      total: string
      field: string
      operator: string
    }
    layoutSection: {
      header: string
      tableHeader: string
      row: string
      totals: string
      footer: string
      custom: string
      labelText: string
    }
    layoutCell: {
      defaultBg: string
      selectedBg: string
      hoverBg: string
      headerBg: string
      headerBorder: string
      resizeGuide: string
      editBorder: string
    }
    agent: {
      panelBg: string
      userBubbleBg: string
      assistantBubbleBg: string
      streamingDot: string
      codeBg: string
    }
    formEditor: {
      canvasBg: string
      elementBorder: string
      elementSelected: string
      elementHover: string
      dropIndicator: string
      groupBorder: string
      groupHeaderBg: string
      tableHeaderBg: string
      handleBg: string
      handleBorder: string
      gridLine: string
      previewInputBg: string
    }
  }
  interface PaletteOptions {
    sqlZone?: { background: string; border: string }
    jsZone?: { background: string; border: string }
    sysProc?: { readonlyBg: string; overriddenBg: string }
    treeNode?: { dirtyDot: string }
    cellType?: {
      text: string
      parameter: string
      total: string
      field: string
      operator: string
    }
    layoutSection?: {
      header: string
      tableHeader: string
      row: string
      totals: string
      footer: string
      custom: string
      labelText: string
    }
    layoutCell?: {
      defaultBg: string
      selectedBg: string
      hoverBg: string
      headerBg: string
      headerBorder: string
      resizeGuide: string
      editBorder: string
    }
    agent?: {
      panelBg: string
      userBubbleBg: string
      assistantBubbleBg: string
      streamingDot: string
      codeBg: string
    }
    formEditor?: {
      canvasBg: string
      elementBorder: string
      elementSelected: string
      elementHover: string
      dropIndicator: string
      groupBorder: string
      groupHeaderBg: string
      tableHeaderBg: string
      handleBg: string
      handleBorder: string
      gridLine: string
      previewInputBg: string
    }
  }
}

// Designer background — slightly warmer than the pure gradient so
// code/tree editors feel comfortable during long sessions.
const DESIGNER_BG   = '#0A0A14'    // matches appColors dark gradient
const DESIGNER_PAPER = '#1C1C2E'   // matches appColors.background.dark.paper base

export const designerTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:   { main: P.main, light: P.light, dark: P.dark, contrastText: '#fff' },
    secondary: { main: S.main, light: S.light, dark: S.dark, contrastText: '#fff' },
    success:   { main: appColors.success.main,  light: appColors.success.light,  dark: appColors.success.dark },
    warning:   { main: appColors.warning.main,  light: appColors.warning.light,  dark: appColors.warning.dark },
    error:     { main: appColors.error.main,    light: appColors.error.light,    dark: appColors.error.dark },
    info:      { main: appColors.info.main,     light: appColors.info.light,     dark: appColors.info.dark },
    background: {
      default: DESIGNER_BG,
      paper: DESIGNER_PAPER,
    },
    text: {
      primary: TX.primary,
      secondary: TX.secondary,
      disabled: TX.disabled,
    },
    divider: appColors.border.dark.default,
    sqlZone: {
      background: alpha(appColors.warning.main, 0.08),
      border: appColors.warning.main,
    },
    jsZone: {
      background: alpha(P.main, 0.06),
      border: P.main,
    },
    sysProc: {
      readonlyBg: alpha('#9E9E9E', 0.07),
      overriddenBg: alpha(P.main, 0.05),
    },
    treeNode: {
      dirtyDot: appColors.warning.main,
    },
    cellType: {
      text: 'transparent',
      parameter: alpha('#FFF9C4', 0.15),
      total: alpha('#C8E6C9', 0.15),
      field: alpha('#BBDEFB', 0.15),
      operator: alpha('#E1BEE7', 0.15),
    },
    layoutSection: {
      header: alpha(appColors.ai.main, 0.25),
      tableHeader: alpha(P.main, 0.2),
      row: alpha(appColors.success.main, 0.18),
      totals: alpha(appColors.warning.main, 0.22),
      footer: alpha(TX.secondary, 0.18),
      custom: alpha(appColors.error.main, 0.18),
      labelText: TX.secondary,
    },
    layoutCell: {
      defaultBg: DESIGNER_PAPER,
      selectedBg: alpha(P.main, 0.12),
      hoverBg: alpha('#ffffff', 0.04),
      headerBg: '#1C1C2E',
      headerBorder: alpha('#ffffff', 0.28),
      resizeGuide: P.main,
      editBorder: P.main,
    },
    agent: {
      panelBg: alpha(DESIGNER_PAPER, 0.98),
      userBubbleBg: alpha(P.main, 0.12),
      assistantBubbleBg: alpha('#FFFFFF', 0.04),
      streamingDot: P.main,
      codeBg: DESIGNER_BG,
    },
    formEditor: {
      canvasBg: DESIGNER_BG,
      elementBorder: alpha('#ffffff', 0.08),
      elementSelected: P.main,
      elementHover: alpha(P.main, 0.4),
      dropIndicator: P.main,
      groupBorder: alpha('#ffffff', 0.12),
      groupHeaderBg: alpha('#ffffff', 0.04),
      tableHeaderBg: alpha('#ffffff', 0.06),
      handleBg: P.main,
      handleBorder: '#ffffff',
      gridLine: alpha(P.main, 0.05),
      previewInputBg: alpha('#ffffff', 0.06),
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
    fontSize: 13,
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: { minHeight: 36, fontSize: 12, textTransform: 'none' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13, padding: '4px 8px' },
      },
    },
    MuiButton: { defaultProps: { size: 'small' } },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiTooltip: { defaultProps: { arrow: true } },
    MuiChip: { defaultProps: { size: 'small' } },
  },
})
